import { useState, useEffect } from 'react';
import { UserPlus, Eye, EyeOff, Copy, Check, RefreshCw, AlertCircle, CreditCard as Edit2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Influencer } from '../types/influencer';
import { useModal } from '../contexts/ModalContext';
import { useLanguage } from '../contexts/LanguageContext';
import { VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY } from '../lib/env';
import { log } from '../lib/logger';

interface InfluencerWithAuth extends Influencer {
  hasAccount: boolean;
  accountEmail?: string;
  accountStatus?: string;
}

interface AccountCredentials {
  email: string;
  password: string;
}

function generateMemorablePassword(name: string): string {
  const cleanName = name.replace(/[^a-zA-Z]/g, '').slice(0, 8);
  const numbers = Math.floor(1000 + Math.random() * 9000);
  return `Mohit2024${cleanName}${numbers}`;
}

export function InfluencerAccountsPanel() {
  const { t, translateError } = useLanguage();
  const { showConfirm, showAlert, toast } = useModal();
  const [influencers, setInfluencers] = useState<InfluencerWithAuth[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Data states
  const [selectedInfluencer, setSelectedInfluencer] = useState<Influencer | null>(null);
  const [customEmail, setCustomEmail] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [createdCredentials, setCreatedCredentials] = useState<AccountCredentials | null>(null);
  const [viewCredentials, setViewCredentials] = useState<AccountCredentials | null>(null);
  const [regeneratedPassword, setRegeneratedPassword] = useState<string>('');

  // Edit form states
  const [editForm, setEditForm] = useState({
    full_name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    iban: '',
    swift_code: '',
    id_number: '',
    vat_number: '',
  });

  // UI states
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadInfluencers();
  }, []);

  const loadInfluencers = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: influencersData, error: influencersError } = await supabase
        .from('influencers')
        .select('*')
        .order('full_name');

      if (influencersError) throw influencersError;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${VITE_SUPABASE_URL}/functions/v1/get-influencer-emails`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      const authMap = result.success ? result.data : {};

      const merged: InfluencerWithAuth[] = (influencersData || []).map((inf) => ({
        ...inf,
        hasAccount: !!authMap[inf.id],
        accountEmail: authMap[inf.id]?.email,
        accountStatus: authMap[inf.id]?.status,
      }));

      setInfluencers(merged);
    } catch (err: any) {
      log.error('Error loading influencers:', err);
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = (influencer: Influencer) => {
    setSelectedInfluencer(influencer);
    setCustomEmail(influencer.email || '');
    setGeneratedPassword(generateMemorablePassword(influencer.full_name));
    setError(null);
    setShowCreateModal(true);
  };

  const handleCreateAccount = async () => {
    if (!selectedInfluencer || !customEmail || !generatedPassword) return;

    try {
      setProcessing(selectedInfluencer.id);
      setError(null);

      // Call Edge Function WITHOUT Authorization header (function uses service role internally)
      const response = await fetch(
        `${VITE_SUPABASE_URL}/functions/v1/influencer-create-account`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            influencer_id: selectedInfluencer.id,
            email: customEmail,
            password: generatedPassword,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create account');
      }

      setCreatedCredentials({
        email: customEmail,
        password: generatedPassword,
      });

      setShowCreateModal(false);
      setShowSuccessModal(true);
      await loadInfluencers();
    } catch (err: any) {
      log.error('Account creation error:', err);
      setError(translateError(err));
    } finally {
      setProcessing(null);
    }
  };

  const handleViewCredentials = async (influencer: Influencer) => {
    try {
      setProcessing(influencer.id);
      setSelectedInfluencer(influencer);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${VITE_SUPABASE_URL}/functions/v1/get-influencer-credentials`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ influencer_id: influencer.id }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load credentials');
      }

      setViewCredentials({
        email: result.data.email,
        password: result.data.password,
      });

      setShowCredentialsModal(true);
    } catch (err: any) {
      setError(translateError(err));
    } finally {
      setProcessing(null);
    }
  };

  const handleEditProfile = (influencer: Influencer) => {
    setSelectedInfluencer(influencer);
    setEditForm({
      full_name: influencer.full_name || '',
      company_name: influencer.company_name || '',
      email: influencer.email || '',
      phone: influencer.phone || '',
      address: influencer.address || '',
      iban: influencer.iban || '',
      swift_code: influencer.swift_code || '',
      id_number: influencer.id_number || '',
      vat_number: influencer.vat_number || '',
    });
    setError(null);
    setShowEditModal(true);
  };

  const handleSaveProfile = async () => {
    if (!selectedInfluencer) return;

    try {
      setProcessing(selectedInfluencer.id);
      setError(null);

      const { error: updateError } = await supabase
        .from('influencers')
        .update({
          full_name: editForm.full_name,
          company_name: editForm.company_name,
          email: editForm.email,
          phone: editForm.phone,
          address: editForm.address,
          iban: editForm.iban,
          swift_code: editForm.swift_code,
          id_number: editForm.id_number,
          vat_number: editForm.vat_number,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedInfluencer.id);

      if (updateError) throw updateError;

      toast.success(t('creatorsHub.toast.profileUpdated'));
      setShowEditModal(false);
      await loadInfluencers();
    } catch (err: any) {
      log.error('Profile update error:', err);
      const errorMsg = translateError(err);
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setProcessing(null);
    }
  };

  const handleRegeneratePassword = async () => {
    if (!selectedInfluencer) return;

    const confirmed = await showConfirm({
      title: 'Regenerate Password?',
      message: `Generate a new password for ${selectedInfluencer.full_name}? The current password will no longer work.`,
      confirmText: 'Regenerate',
      cancelText: 'Cancel',
      variant: 'warning',
    });

    if (!confirmed) return;

    try {
      setProcessing(selectedInfluencer.id);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${VITE_SUPABASE_URL}/functions/v1/admin-reset-password`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ influencer_id: selectedInfluencer.id }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to regenerate password');
      }

      setRegeneratedPassword(result.data.password);
      setViewCredentials({
        email: viewCredentials?.email || '',
        password: result.data.password,
      });

      toast.success(t('creatorsHub.toast.passwordRegenerated'));
    } catch (err: any) {
      log.error('Password regeneration error:', err);
      const errorMsg = translateError(err);
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setProcessing(null);
    }
  };

  const handleCopyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      log.error('Failed to copy:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{t('admin.creatorAccountsTitle')}</h2>
        <p className="text-sm text-gray-600 mt-1">{t('admin.creatorAccountsSubtitle')}</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">×</button>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.creatorAccountsTableCreator')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.creatorAccountsTableEmail')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.creatorAccountsTableStatus')}</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('admin.creatorAccountsTableActions')}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(influencers || []).map((inf) => (
              <tr key={inf.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{inf.full_name}</div>
                  {inf.company_name && <div className="text-sm text-gray-500">{inf.company_name}</div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{inf.accountEmail || inf.email || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {inf.hasAccount ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {t('admin.creatorAccountsStatusActive')}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">{t('admin.creatorAccountsStatusNoAccount')}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    {!inf.hasAccount ? (
                      <button
                        onClick={() => handleOpenCreateModal(inf)}
                        disabled={processing === inf.id}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-xl text-white bg-accent-600 hover:bg-accent-700 disabled:opacity-50"
                      >
                        {processing === inf.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" />{t('admin.creatorAccountsCreate')}</>}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleViewCredentials(inf)}
                          disabled={processing === inf.id}
                          className="px-3 py-1.5 border border-gray-300 text-xs rounded-xl hover:bg-gray-50"
                          title="View Credentials"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditProfile(inf)}
                          disabled={processing === inf.id}
                          className="px-3 py-1.5 border border-gray-300 text-xs rounded-xl hover:bg-gray-50"
                          title="Edit Profile"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && selectedInfluencer && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">{t('admin.creatorAccountsCreateModalTitle').replace('{0}', selectedInfluencer.full_name)}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Password</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={generatedPassword}
                    onChange={(e) => setGeneratedPassword(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-xl font-mono text-sm"
                  />
                  <button
                    onClick={() => setGeneratedPassword(generateMemorablePassword(selectedInfluencer.full_name))}
                    className="px-3 py-2 border rounded-xl hover:bg-gray-50"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 border rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAccount}
                disabled={!customEmail || !generatedPassword || !!processing}
                className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-xl hover:bg-accent-700 disabled:opacity-50 transition-transform hover:scale-[1.02]"
              >
                {processing ? t('admin.creatorAccountsCreating') : t('admin.creatorAccountsCreateModalButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && createdCredentials && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 max-w-md w-full p-6">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">{t('admin.creatorAccountsSuccessTitle')}</h3>
            </div>
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Email:</span>
                  <button onClick={() => handleCopyToClipboard(createdCredentials.email, 'email')} className="text-blue-600">
                    {copiedField === 'email' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-sm font-mono break-all">{createdCredentials.email}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Password:</span>
                  <button onClick={() => handleCopyToClipboard(createdCredentials.password, 'password')} className="text-blue-600">
                    {copiedField === 'password' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-sm font-mono break-all">{createdCredentials.password}</p>
              </div>
            </div>
            <button
              onClick={() => { setShowSuccessModal(false); setCreatedCredentials(null); }}
              className="w-full mt-4 px-4 py-2 bg-accent-600 text-white rounded-xl hover:bg-accent-700 transition-transform hover:scale-[1.02]"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* View Credentials Modal */}
      {showCredentialsModal && viewCredentials && selectedInfluencer && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Access Credentials</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">Portal Link:</span>
                  <button
                    onClick={() => handleCopyToClipboard('app.mohit.pt/creatorshub', 'url')}
                    className="text-blue-600 hover:text-blue-800"
                    title="Copy URL"
                  >
                    {copiedField === 'url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-sm font-mono text-blue-800">app.mohit.pt/creatorshub</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Username:</span>
                  <button
                    onClick={() => handleCopyToClipboard(viewCredentials.email, 'view-email')}
                    className="text-blue-600 hover:text-blue-800"
                    title="Copy email"
                  >
                    {copiedField === 'view-email' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-sm font-mono break-all">{viewCredentials.email}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Password:</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-500 hover:text-gray-700"
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    {(regeneratedPassword || showPassword) && (
                      <button
                        onClick={() => handleCopyToClipboard(regeneratedPassword || viewCredentials.password, 'view-password')}
                        className="text-blue-600 hover:text-blue-800"
                        title="Copy password"
                      >
                        {copiedField === 'view-password' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-mono flex-1">
                    {regeneratedPassword || (showPassword ? viewCredentials.password : '••••••••')}
                  </p>
                  <button
                    onClick={handleRegeneratePassword}
                    disabled={!!processing}
                    className="px-3 py-1.5 text-xs font-medium rounded-xl border border-gray-300 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
                    title="Regenerate password"
                  >
                    {processing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Regenerate
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setShowCredentialsModal(false);
                setViewCredentials(null);
                setRegeneratedPassword('');
                setSelectedInfluencer(null);
                setShowPassword(false);
              }}
              className="w-full mt-4 px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditModal && selectedInfluencer && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Profile: {selectedInfluencer.full_name}</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Company Name</label>
                  <input
                    type="text"
                    value={editForm.company_name}
                    onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <textarea
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  rows={2}
                />
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 text-gray-700">Financial Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">IBAN</label>
                    <input
                      type="text"
                      value={editForm.iban}
                      onChange={(e) => setEditForm({ ...editForm, iban: e.target.value })}
                      className="w-full px-3 py-2 border rounded-xl font-mono text-sm"
                      placeholder="PT50000000000000000000000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">SWIFT/BIC</label>
                    <input
                      type="text"
                      value={editForm.swift_code}
                      onChange={(e) => setEditForm({ ...editForm, swift_code: e.target.value })}
                      className="w-full px-3 py-2 border rounded-xl font-mono text-sm"
                      placeholder="BOFAPTPL"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tax ID / NIF</label>
                    <input
                      type="text"
                      value={editForm.id_number}
                      onChange={(e) => setEditForm({ ...editForm, id_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="123456789"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">VAT Number</label>
                    <input
                      type="text"
                      value={editForm.vat_number}
                      onChange={(e) => setEditForm({ ...editForm, vat_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="PT123456789"
                    />
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedInfluencer(null);
                  setError(null);
                }}
                className="flex-1 px-4 py-2 border rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={!editForm.full_name || !!processing}
                className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-xl hover:bg-accent-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
              >
                {processing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
